import { DatePipe } from "@angular/common";
import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Coupon, CouponPage, CouponSortField } from "@models/coupon";
import { SnackBarService } from "@services/snack-bar.service";
import { PaginatorModule, PaginatorState } from "primeng/paginator";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";

interface CouponSortColumn {
  field: CouponSortField;
  label: string;
}

@Component({
  selector: "app-coupon-table",
  imports: [
    DatePipe,
    TableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    PaginatorModule,
    ProgressSpinnerModule,
    SkeletonModule,
  ],
  templateUrl: "./coupon-table.component.html",
  styleUrl: "./coupon-table.component.scss",
})
export class CouponTableComponent {
  private readonly snackbar = inject(SnackBarService);

  first = 0;
  rows = 10;

  readonly columns: CouponSortColumn[] = [
    { field: "code", label: "CÓDIGO" },
    { field: "description", label: "DESCRIÇÃO" },
  ];

  @Input({ required: true }) data: CouponPage | null = null;
  @Input() filtered = false;
  @Input() loading = false;
  @Input() sort: CouponSortField | null = null;
  @Input() direction: "asc" | "desc" = "desc";
  @Input() readOnly = false;

  @Output() viewDetailsClick = new EventEmitter<Coupon>();
  @Output() editClick = new EventEmitter<Coupon>();
  @Output() deactivateClick = new EventEmitter<Coupon>();
  @Output() sortChange = new EventEmitter<CouponSortField>();
  @Output() pageChange = new EventEmitter<PaginatorState>();

  sortIcon(field: CouponSortField): string {
    if (this.sort !== field) return "pi pi-sort-alt";
    return this.direction === "asc"
      ? "pi pi-sort-amount-up"
      : "pi pi-sort-amount-down";
  }

  // Desativar só faz sentido sem uso; com uso a ação correta é Encerrar (no
  // detalhe). Mantém o ícone desabilitado para cupons já utilizados/encerrados.
  canDeactivate(coupon: Coupon): boolean {
    return coupon.status === "active" && coupon.used_count === 0;
  }

  canEdit(coupon: Coupon): boolean {
    return coupon.status === "active";
  }

  affiliateLink(coupon: Coupon): string {
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://www.yes7tickets.net";
    return `${origin}/event/${coupon.event_id}?cupom=${encodeURIComponent(coupon.code)}`;
  }

  async copyAffiliateLink(coupon: Coupon): Promise<void> {
    const link = this.affiliateLink(coupon);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const input = document.createElement("textarea");
        input.value = link;
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      this.snackbar.openSnackBar("Link do cupom copiado.", "Fechar", "success");
    } catch {
      this.snackbar.openSnackBar(
        "Não foi possível copiar o link.",
        "Fechar",
        "error",
      );
    }
  }
}
